/**
 * Backend Configuration
 *
 * Reads backend URL and API key from environment variables.
 * No fallbacks, no hardcoded values - pure configuration from environment.
 */

export interface BackendConfig {
    baseUrl: string;
    apiKey: string;
    /** Base URL for file uploads. Bypasses Fastly CDN in production. Falls back to baseUrl if not set. */
    fileUploadBaseUrl: string;
}

/**
 * Get backend configuration from environment variables.
 *
 * Required environment variables:
 * - BEAT_API_BASE_URL or VITE_BEAT_API_BASE_URL
 * - BEAT_API_KEY or VITE_BEAT_API_KEY
 *
 * @throws {Error} If required environment variables are not set
 * @returns {BackendConfig} Backend configuration
 *
 * @example
 * // .env.development
 * BEAT_API_BASE_URL=https://beat-dev.balloon-events.com
 * BEAT_API_KEY=your-dev-key
 *
 * // Usage
 * const { baseUrl, apiKey } = getBackendConfig();
 * const response = await fetch(`${baseUrl}/api/events`);
 */
export function getBackendConfig(): BackendConfig {
    // Check all environment variable formats:
    // - Node.js server-side: process.env.BEAT_API_BASE_URL
    // - Next.js client-side: process.env.NEXT_PUBLIC_BEAT_API_BASE_URL
    // - Vite: import.meta.env.VITE_BEAT_API_BASE_URL
    const baseUrl = import.meta.env.VITE_BEAT_API_BASE_URL
    const apiKey = import.meta.env.VITE_BEAT_API_KEY

    if (!baseUrl) {
        throw new Error(
            'BEAT_API_BASE_URL is required but not set.\n' +
            'Please set it in your .env file:\n' +
            '  Vite: VITE_BEAT_API_BASE_URL=https://beat-dev.balloon-events.com'
        );
    }

    if (!apiKey) {
        throw new Error(
            'BEAT_API_KEY is required but not set.\n' +
            'Please set it in your .env file:\n' +
            '  BEAT_API_KEY=your-api-key\n' +
            '  VITE_BEAT_API_KEY=your-api-key'
        );
    }

    // Optional: separate base URL for file uploads to bypass CDN
    const fileUploadBaseUrl = import.meta.env.VITE_FILE_UPLOAD_BASE_URL || baseUrl;

    return {
        baseUrl,
        apiKey,
        fileUploadBaseUrl,
    };
}

/**
 * Get frontend base URL from environment variables.
 *
 * This is used for cross-app navigation (e.g., linking from embed to main site).
 *
 * Required environment variables:
 * - NEXT_PUBLIC_FRONTEND_BASE_URL (Next.js)
 * - VITE_FRONTEND_BASE_URL (Vite)
 *
 * @returns {string} Frontend base URL (e.g., "https://balloon-events.com")
 *
 * @example
 * const frontendUrl = getFrontendUrl();
 * const eventLink = `${frontendUrl}/event/${slug}`;
 */
export function getFrontendUrl(): string {
    const frontendUrl = import.meta.env.VITE_FRONTEND_BASE_URL

    if (!frontendUrl) {
        console.warn(
            'FRONTEND_BASE_URL is not set. Falling back to default.\n' +
            'Set VITE_FRONTEND_BASE_URL in your .env file.'
        );
        return 'https://balloon-events.com';
    }

    return frontendUrl;
}
